import { Link, type LinkProps } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { StatGroup } from "@/deps/ui-library";

export interface Kpi {
  label: string;
  value: string;
  /** A short qualifier under the figure — "3 overdue", "since Shrawan". */
  detail?: string;
  /** Where the figure comes from. A tile that cannot be opened is a figure nobody can check. */
  to?: LinkProps["to"];
  search?: LinkProps["search"];
  tone?: "default" | "warn" | "bad";
}

/** `Link` with its props pinned to `LinkProps`, so the tile's polymorphic props stay checkable. */
const TileLink = (props: LinkProps & { className?: string; children?: ReactNode }) => (
  <Link {...props} />
);

const TONE = { default: "default", warn: "warning", bad: "danger" } as const;

/**
 * The row of figures at the top of a screen: RDS `StatGroup`. What is left here is the router —
 * each tile is a TanStack `Link` to the list its figure is added up from, which RDS cannot know.
 */
export function KpiStrip({ items, loading = false }: { items: Kpi[]; loading?: boolean }) {
  return (
    <StatGroup.Root loading={loading}>
      {items.map((kpi) =>
        kpi.to ? (
          <StatGroup.Item
            key={kpi.label}
            as={TileLink}
            to={kpi.to}
            search={kpi.search}
            label={kpi.label}
            value={kpi.value}
            detail={kpi.detail}
            tone={TONE[kpi.tone ?? "default"]}
          />
        ) : (
          <StatGroup.Item
            key={kpi.label}
            label={kpi.label}
            value={kpi.value}
            detail={kpi.detail}
            tone={TONE[kpi.tone ?? "default"]}
          />
        ),
      )}
    </StatGroup.Root>
  );
}
